import { useState } from "react";
import { useTranslation } from "react-i18next";
import { config } from "configs/config";
import { Section, SettingRow } from "./SettingsControls";
import type { ReportIntervalUnit, ReportMonthDay } from "./useSettings";

type SchedulePreset = "daily" | "weekly" | "nweeks" | "monthly" | "nmonths";

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] as const; // Mon–Sun
const WEEKDAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

type Props = {
    intervalAmount: number;
    intervalUnit: ReportIntervalUnit;
    weekday: number;
    monthDay: ReportMonthDay;
    saved?: boolean;
    dirty?: boolean;
    onAmountChange: (v: number) => void;
    onUnitChange: (v: ReportIntervalUnit) => void;
    onWeekdayChange: (v: number) => void;
    onMonthDayChange: (v: ReportMonthDay) => void;
    onSave?: () => void;
};

function derivePreset(amount: number, unit: ReportIntervalUnit): SchedulePreset {
    if (unit === "days")   return "daily";
    if (unit === "weeks")  return amount === 1 ? "weekly"  : "nweeks";
    if (unit === "months") return amount === 1 ? "monthly" : "nmonths";
    return "monthly";
}

export function ReportSettings({
    intervalAmount,
    intervalUnit,
    weekday,
    monthDay,
    saved,
    dirty,
    onAmountChange,
    onUnitChange,
    onWeekdayChange,
    onMonthDayChange,
    onSave,
}: Props) {
    const { t } = useTranslation();
    const [generating, setGenerating] = useState(false);

    const preset = derivePreset(intervalAmount, intervalUnit);

    const handlePreset = (p: SchedulePreset) => {
        if (p === "daily")   { onUnitChange("days");   onAmountChange(1); }
        if (p === "weekly")  { onUnitChange("weeks");  onAmountChange(1); }
        if (p === "nweeks")  { onUnitChange("weeks");  onAmountChange(intervalUnit === "weeks" && intervalAmount > 1 ? intervalAmount : 2); }
        if (p === "monthly") { onUnitChange("months"); onAmountChange(1); }
        if (p === "nmonths") { onUnitChange("months"); onAmountChange(intervalUnit === "months" && intervalAmount > 1 ? intervalAmount : 2); }
    };

    const PRESETS: { key: SchedulePreset; label: string }[] = [
        { key: "daily",   label: t("settings.everyDay") },
        { key: "weekly",  label: t("settings.everyWeek") },
        { key: "nweeks",  label: t("settings.everyNWeeks") },
        { key: "monthly", label: t("settings.everyMonth") },
        { key: "nmonths", label: t("settings.everyNMonths") },
    ];

    const showWeekday  = preset === "weekly" || preset === "nweeks";
    const showMonthDay = preset === "monthly" || preset === "nmonths";
    const showAmount   = preset === "nweeks" || preset === "nmonths";

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const token = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;

            const res = await fetch(`${config.baseUrl}/api/settings/report`, {
                method: "GET",
                headers: {
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
            });

            if (!res.ok) throw new Error("Failed to generate report");

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `report-${new Date().toISOString().slice(0, 10)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Report generation failed", e);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Section
            title={t("settings.reports")}
            actions={onSave ? (
                <button
                    className="btn-outline"
                    onClick={onSave}
                    disabled={saved || !dirty}
                >
                    {saved ? t("settings.saved") : t("settings.saveChanges")}
                </button>
            ) : undefined}
        >
            <SettingRow
                title={t("settings.reportInterval")}
                description={t("settings.reportIntervalDesc")}
            >
                <div className="report-schedule">
                    <div className="toggle-group report-schedule__presets">
                        {PRESETS.map(({ key, label }) => (
                            <button
                                key={key}
                                className={`toggle-btn${preset === key ? " toggle-btn--active" : ""}`}
                                onClick={() => handlePreset(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {showAmount && (
                        <div className="report-schedule__row">
                            <input
                                type="number"
                                min={2}
                                value={intervalAmount}
                                onChange={(e) => onAmountChange(Math.max(2, parseInt(e.target.value) || 2))}
                                className="text-input report-schedule__amount"
                            />
                            <span className="settings-unit">
                                {preset === "nweeks" ? t("settings.weeksUnit") : t("settings.monthsUnit")}
                            </span>
                        </div>
                    )}

                    {showWeekday && (
                        <div className="toggle-group">
                            {WEEKDAYS.map((day, i) => (
                                <button
                                    key={day}
                                    className={`toggle-btn${weekday === day ? " toggle-btn--active" : ""}`}
                                    onClick={() => onWeekdayChange(day)}
                                >
                                    {t(`settings.${WEEKDAY_KEYS[i]}`)}
                                </button>
                            ))}
                        </div>
                    )}

                    {showMonthDay && (
                        <div className="toggle-group">
                            {(["first", "last"] as ReportMonthDay[]).map((v) => (
                                <button
                                    key={v}
                                    className={`toggle-btn${monthDay === v ? " toggle-btn--active" : ""}`}
                                    onClick={() => onMonthDayChange(v)}
                                >
                                    {t(`settings.${v}DayOfMonth`)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </SettingRow>

            <SettingRow
                title={t("settings.generateReport")}
                description={t("settings.generateReportDesc")}
            >
                <button
                    className="btn-outline"
                    onClick={handleGenerate}
                    disabled={generating}
                >
                    {generating ? t("settings.generating") : t("settings.generateNow")}
                </button>
            </SettingRow>
        </Section>
    );
}
